/**
 * Coerce a token-amount value to a string.
 *
 * All token amounts are stored as strings in smallest units (e.g. "1000000"
 * for 1 USDC). This helper normalises any runtime representation to that
 * canonical string form.
 *
 * Numbers are stringified as-is — no ×1e6 heuristic is applied because there
 * is no production data with legacy dollar-denominated numeric values.  If
 * such values ever appear in the future, a targeted DB migration should be
 * used instead of guessing in application code.
 */
export function tokenAmountToString(val: unknown): string | undefined {
  if (val === undefined || val === null) return undefined;
  return String(val);
}

/**
 * Plain decimal (optionally leading with ".") with no sign, no scientific
 * notation, no separators. Used to validate humanToAtomic input.
 */
const DECIMAL_RE = /^(\d+(\.\d+)?|\.\d+)$/;

/**
 * Convert a human-readable decimal amount string to the token's atomic
 * representation using BigInt-safe string math, so 18-decimal tokens keep
 * full precision past `Number.MAX_SAFE_INTEGER`.
 *
 * Returns the atomic value as a canonical (no leading-zero) decimal string,
 * or `null` if the input is invalid — empty, non-numeric, negative,
 * scientific-notation, or has more fractional digits than the token supports.
 *
 * Canonicalisation: leading zeros in the whole part are stripped
 * (`humanToAtomic("001", 6) === "1000000"`); zero is rendered as `"0"`.
 */
export function humanToAtomic(human: string, decimals: number): string | null {
  if (!human) return null;
  if (!Number.isInteger(decimals) || decimals < 0) return null;
  if (!DECIMAL_RE.test(human)) return null;

  const dotIdx = human.indexOf(".");
  const whole = dotIdx === -1 ? human : human.slice(0, dotIdx);
  const frac = dotIdx === -1 ? "" : human.slice(dotIdx + 1);

  if (frac.length > decimals) return null;

  const paddedFrac = frac.padEnd(decimals, "0");
  const combined = (whole || "0") + paddedFrac;
  const normalised = combined.replace(/^0+/, "") || "0";
  return normalised;
}

/**
 * Convert an atomic amount string back to a human-readable decimal string.
 * Trims trailing zeros in the fractional part and drops the decimal point
 * entirely when the value is whole.
 *
 * Returns `""` for empty input or any non-integer string — callers treat
 * this as "no value", matching the csvTable empty-cell semantics.
 */
export function atomicToHuman(atomic: string, decimals: number): string {
  if (!atomic) return "";
  if (!/^\d+$/.test(atomic)) return "";
  if (!Number.isInteger(decimals) || decimals < 0) return "";

  if (decimals === 0) {
    return atomic.replace(/^0+/, "") || "0";
  }

  const padded = atomic.padStart(decimals + 1, "0");
  const whole = padded.slice(0, -decimals).replace(/^0+/, "") || "0";
  const frac = padded.slice(-decimals).replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole;
}

/**
 * Convert a csvTable prop's human-unit values to atomic for submit time.
 *
 * The cell stores whatever the user typed (e.g. "1.5"); downstream actions
 * expect atomic units (e.g. "1500000"). This helper walks each declared
 * tokenAmount column and scales every value through `humanToAtomic` using
 * the column's resolved decimals.
 *
 * Non-tokenAmount columns are left untouched — they flow through unchanged.
 *
 * Returns a new columnar-value object on success. Returns `null` when any
 * value in any tokenAmount column fails to convert — caller should block
 * submit; the cell-level validator should already have flagged the input.
 */
export function resolveCsvTableHumanToAtomic(
  columnarValue: Record<string, unknown[]>,
  tokenAmountColumns: Array<{ key: string; decimals: number }>,
): Record<string, unknown[]> | null {
  const out: Record<string, unknown[]> = { ...columnarValue };
  for (const { key, decimals } of tokenAmountColumns) {
    const col = columnarValue[key];
    if (!Array.isArray(col)) continue;
    const converted: unknown[] = [];
    for (const v of col) {
      // Empty cells stay empty — the grid strips entirely-empty rows on
      // commit, but single empty cells within populated rows can still
      // reach us and must be preserved (caller's required-check will have
      // already blocked submission if those cells were mandatory).
      if (v === "" || v === null || v === undefined) {
        converted.push("");
        continue;
      }
      const atomic = humanToAtomic(String(v), decimals);
      if (atomic === null) return null;
      converted.push(atomic);
    }
    out[key] = converted;
  }
  return out;
}
